import { graphql } from "@acto/gql";
import { PaginatedDataAccessResult, PagingOptions } from "@acto/taskpane/core/data-access/types";
import { useMemo } from "react";
import { GetChatBookmarksQuery } from "@acto/gql/graphql";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";
import { ChatBookmark } from "@acto/taskpane/core/domain";

const ChatBookmarksQuery = graphql(`
  query GetChatBookmarks($first: Int!, $after: String, $filter: ChatBookmarkFilter!) {
    chatBookmarks(first: $first, after: $after, filter: $filter) {
      __typename
      edges {
        __typename
        cursor
        node {
          id
          __typename
          chatHistory {
            id
            __typename
            client {
              id
              __typename
              name
              externalReference
            }
            sessionId
            messages
            createdAt
            updatedAt
          }
          client {
            id
            __typename
            name
            externalReference
          }
          messageIndex
          title
          subtitle
          userDetailsId
          createdAt
        }
      }
      pageInfo {
        __typename
        hasNextPage
        endCursor
      }
    }
  }
`);

interface ChatBookmarkFilterOptions {
  customerId: string;
  chatHistoryId?: string;
}

export function useChatBookmarks({
  customerId,
  chatHistoryId,
  limit = 50, // Higher default since bookmarks are limited
}: PagingOptions & ChatBookmarkFilterOptions): PaginatedDataAccessResult<ChatBookmark> {
  const filter = useMemo(
    () => ({
      clientId: customerId,
      chatHistoryId,
    }),
    [customerId, chatHistoryId],
  );

  const { data, isFetching, isPending, error, hasNextPage, fetchNextPage, refreshAfter, refetch } = usePagination({
    query: ChatBookmarksQuery,
    limit,
    connection: (data: GetChatBookmarksQuery) => data.chatBookmarks,
    variables: { filter },
  });

  const chatBookmarks = useMemo(() => data?.map(ChatBookmark.fromGraphql), [data]);

  return {
    data: chatBookmarks,
    isFetching,
    isPending,
    error,
    hasNextPage,
    fetchNextPage,
    refreshAfter,
    refetch,
  };
}
